import React from 'react';

type ShowProps = {
  /**
   * Condition to determine whether to render children or fallback.
   * If a function is provided, it will be called to get the boolean value.
   */
  when: boolean | (() => boolean);
  /**
   * Content to render when the condition is true.
   */
  children: React.ReactNode;
  /**
   * Optional content to render when the condition is false.
   * If not provided, nothing will be rendered when the condition is false.
   */
  fallback?: React.ReactNode;
};

/**
 * Conditional rendering component inspired by SolidJS's Show.
 * Renders `children` if the `when` condition is true, otherwise renders `fallback`.
 *
 * @param {ShowProps} props - Props for Show component.
 * @returns {React.ReactNode} Rendered children or fallback.
 *
 * @see https://docs.solidjs.com/concepts/control-flow/conditional-rendering#show
 */
const Show = ({ when, children, fallback }: ShowProps): React.ReactNode => {
  if (typeof when === 'function' ? when() : when) {
    return <>{children}</>;
  }
  return fallback || null;
};

export default Show;
